import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useQueryClient } from "@tanstack/react-query";
import { useApiMutation } from "../../../../common/infrastructure/hooks/useApiMutation";
import { useApiQuery } from "../../../../common/infrastructure/hooks/useApiQuery";
import { useFormValidationMessages } from "../../../../common/infrastructure/hooks/useFormHelper";
import { useAppContext } from "../../../../AppContext";
import { endpointsMetadata } from "../../../../common/infrastructure/routes/endpointsMetadata";
import { TransferStatus } from "../../../../common/enums";
import {
  IInventoryTransferFormModel,
  IInventoryTransferLineFormModel,
  IInventoryTransferListItemModel,
  formModelToPayload,
  getInventoryTransferDefaults,
  inventoryTransferSchema,
  viewModelToFormModel,
} from "../models/inventoryTransferModels";
import { useTransferDialogPermissions } from "./useTransferDialogPermissions";

export type TransferDialogMode =
  | "create"
  | "editPlanned"
  | "editCompleted"
  | "readonly";

interface IUseDialogArgs {
  editingTransferId: number | null;
  isOpen: boolean;
  onClose: () => void;
  initialLines?: IInventoryTransferLineFormModel[] | null;
}

/**
 * Owns the Доставки create/edit dialog logic:
 *  - Loads the transfer detail (when editing)
 *  - Sets up react-hook-form with default or loaded values
 *  - Exposes a submit handler that calls the correct Create/Update endpoint
 *  - Decides the dialog "mode" based on current user role + existing status
 *  - Disables the form when the user cannot edit (readonly mode)
 */
export const useInventoryTransferDialog = ({
  editingTransferId,
  isOpen,
  onClose,
  initialLines,
}: IUseDialogArgs) => {
  const { selectedStudioId } = useAppContext();
  const endpoints = endpointsMetadata.inventoryTransfers;
  const queryClient = useQueryClient();
  const permissions = useTransferDialogPermissions();

  const isEditing = editingTransferId != null;

  const { data: existingTransfer } = useApiQuery<IInventoryTransferListItemModel>({
    queryKey: [endpoints.getById, editingTransferId, selectedStudioId],
    endpoint: `${endpoints.getById}?id=${editingTransferId ?? 0}`,
    enabled: isOpen && isEditing && selectedStudioId > 0,
    globalIsLoading: false,
  });

  const form = useForm<IInventoryTransferFormModel>({
    resolver: yupResolver(inventoryTransferSchema) as any,
    defaultValues: getInventoryTransferDefaults(),
    mode: "onBlur",
  });

  useFormValidationMessages(form);

  // Reset the form whenever the dialog opens, the edited id changes, or the
  // existing transfer finishes loading.
  useEffect(() => {
    if (!isOpen) return;

    if (!isEditing) {
      const defaults = getInventoryTransferDefaults();
      if (initialLines && initialLines.length > 0) {
        defaults.lines = initialLines;
      }
      form.reset(defaults);
      return;
    }

    if (existingTransfer) {
      form.reset(viewModelToFormModel(existingTransfer));
    }
  }, [isOpen, isEditing, existingTransfer, form, initialLines]);

  const mode: TransferDialogMode = useMemo(() => {
    if (!isEditing) return "create";
    if (!existingTransfer) return "readonly"; // still loading, lock the form briefly
    const allowed = permissions.canEditStatus(existingTransfer.status);
    if (!allowed) return "readonly";
    return existingTransfer.status === TransferStatus.Completed
      ? "editCompleted"
      : "editPlanned";
  }, [isEditing, existingTransfer, permissions]);

  const isFormDisabled = mode === "readonly";

  const invalidateTransferQueries = () => {
    queryClient.invalidateQueries({ queryKey: [endpoints.getAll, selectedStudioId] });
    queryClient.invalidateQueries({ queryKey: [endpoints.getAllWithDeleted, selectedStudioId] });
    queryClient.invalidateQueries({
      queryKey: [endpointsMetadata.inventoryItemNomenclature.getAllCategorized, selectedStudioId],
    });
  };

  const createMutation = useApiMutation<string>({
    onSuccess: () => {
      invalidateTransferQueries();
      onClose();
    },
  });

  const updateMutation = useApiMutation<string>({
    onSuccess: () => {
      invalidateTransferQueries();
      if (editingTransferId != null) {
        queryClient.invalidateQueries({
          queryKey: [endpoints.getById, editingTransferId, selectedStudioId],
        });
      }
      onClose();
    },
  });

  const handleSubmit = form.handleSubmit((values) => {
    const payload = formModelToPayload(values);

    if (!isEditing) {
      createMutation.mutate({
        endpoint: endpoints.create,
        method: "post",
        payload,
      });
    } else {
      updateMutation.mutate({
        endpoint: endpoints.update,
        method: "put",
        additionalParams: `id=${editingTransferId}`,
        payload,
      });
    }
  });

  return {
    form,
    mode,
    isFormDisabled,
    existingTransfer,
    handleSubmit,
    isSubmitting: createMutation.isLoading || updateMutation.isLoading,
    permissions,
  };
};
